<template>
  <svg class="artwork" :x="x" :y="y" :width="`${widthInInches}in`" :height="`${heightInInches}in`" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <clipPath :id="`${uuid}-clip`">
        <rect 
          v-if="isDigitalFrameWithoutMat"
          :x="rabbetWidth" 
          :y="rabbetWidth" 
          :width="widthInInches - (rabbetWidth * 2)" 
          :height="heightInInches - (rabbetWidth * 2)" 
        />
        <rect 
          v-else
          :x="0" 
          :y="0" 
          :width="widthInInches" 
          :height="heightInInches" 
        />
      </clipPath>
    </defs>
    <rect v-if="allowUpload" x="0" y="0" :width="widthInInches" :height="heightInInches" fill="transparent" @click="$emit('upload-click')" class="clickable-area"/>
    <g v-if="allowUpload" :transform="`translate(${centerX}, ${centerY})`" @click="$emit('upload-click')">
      <circle class="allow-upload-icon" cx="0" cy="0" r="0.65" fill="transparent" stroke="#1d2019" stroke-width="0.035" />
      <line x1="-0.25" y1="0" x2="0.25" y2="0" stroke="#1d2019" stroke-width="0.035" />
      <line x1="0" y1="-0.25" x2="0" y2="0.25" stroke="#1d2019" stroke-width="0.035" />
    </g>
    <template v-else>
      <image :href="imageUrl" :x="imageX" :y="imageY" :width="imageWidth" :height="imageHeight" :clip-path="`url(#${uuid}-clip)`" />
    </template>

    <rect v-if="loading && !readonly" class="artwork-overlay" :width="widthInInches" :height="heightInInches"></rect>
    <g v-if="loading && !readonly" class="artwork-spinner">
      <svg xmlns="http://www.w3.org/2000/svg" :x="centerX - 0.5" :y="centerY - 0.5" :width="1" :height="1" viewBox="0 0 48 48">
        <path class="a" d="M24,3a.9.9,0,0,0-1,1V14a1,1,0,0,0,2,0V4A.9.9,0,0,0,24,3Z"/>
        <path class="a" d="M44,23H34a1,1,0,0,0,0,2H44a1,1,0,0,0,0-2Z"/>
        <path class="a" d="M31.7,30.3a1,1,0,0,0-1.4,1.4l7,7a1,1,0,0,0,1.4,0,1,1,0,0,0,0-1.4Z"/>
        <path class="a" d="M24,33a.9.9,0,0,0-1,1V44a1,1,0,0,0,2,0V34A.9.9,0,0,0,24,33Z"/>
        <path class="a" d="M16.3,30.3l-7,7a1,1,0,0,0,0,1.4,1,1,0,0,0,1.4,0l7-7a1,1,0,0,0-1.4-1.4Z"/>
        <path class="a" d="M15,24a.9.9,0,0,0-1-1H4a1,1,0,0,0,0,2H14A.9.9,0,0,0,15,24Z"/>
        <path class="a" d="M10.7,9.3a1,1,0,0,0-1.4,1.4l7,7a1,1,0,0,0,1.4,0,1,1,0,0,0,0-1.4Z"/>
        <path class="a" d="M31,18a1.1,1.1,0,0,0,.7-.3l7-7a1,1,0,1,0-1.4-1.4l-7,7a1,1,0,0,0,0,1.4A.9.9,0,0,0,31,18Z"/>
      </svg>
    </g>
    <svg 
      v-if="!imageUrl && !loading && !readonly" 
      class="artwork-add" 
      xmlns="http://www.w3.org/2000/svg" 
      :x="centerX - (widthInInches * 0.2)" 
      :y="centerY - (heightInInches * 0.2)" 
      :width="widthInInches * 0.4" 
      :height="heightInInches * 0.4" 
      preserveAspectRatio="xMidYMid meet" 
      viewBox="0 0 48 48"
    >
      <path class="a" d="M35,23H25V13a1,1,0,0,0-2,0V23H13a1,1,0,0,0,0,2H23V35a1,1,0,0,0,2,0V25H35a1,1,0,0,0,0-2Z"/>
    </svg>
  </svg>
</template>

<script>
import uuidv4 from "uuid/v4";
import f10Icons from "@framebridge/f10-icons/vue/F10Icon.vue";

export default {
  components: {
    f10Icons
  },
  props: {
    artworkKey: {
      type: Number,
      default: 0
    },
    moulding: {
      type: Object,
      required: false
    },
    frameSpec: {
      type: Object,
      required: true
    },
    x: {
      type: Number,
      default: 0
    },
    y: {
      type: Number,
      default: 0
    },
    allowUpload: {
      type: Boolean,
      default: false
    },
    artworkUrl: {
      type: String,
      default: ""
    },
    loading: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      svgWidth: 0,
      svgHeight: 0
    };
  },
  mounted() {
    const svgRect = this.$el.getBoundingClientRect();
    this.svgWidth = svgRect.width;
    this.svgHeight = svgRect.height;
  },
  computed: {
    uuid() {
      return uuidv4();
    },
    artwork() {
      if (this.frameSpec) {
        return this.frameSpec.artworks[this.artworkKey];
      } else {
        return {};
      }
    },
    imageIsPng() {
      if (!this.frameSpec) return "";

      const customerSpecifiedHasPng = this.artwork.customerSpecified && this.artwork.customerSpecified.url.includes(".png");
      const originalUneditedHasPng = this.artwork.originalUnedited && this.artwork.originalUnedited.url.includes(".png");

      return customerSpecifiedHasPng || originalUneditedHasPng;
    },
    imageUrl() {
      if (!this.frameSpec) return "";

      if (this.artworkUrl) {
        return this.artworkUrl;
      }

      const croppedAndNormalizedUrl = this.artwork.croppedAndNormalized && this.artwork.croppedAndNormalized.url;
      const customerSpecifiedUrl = this.artwork.customerSpecified && this.artwork.customerSpecified.url;

      // Digital PNGs print on white paper, so use the opaque JPEG (customerSpecified)
      // rather than the transparent croppedAndNormalized PNG.
      if (this.imageIsPng && this.frameSpec.isDigital()) {
        return customerSpecifiedUrl || croppedAndNormalizedUrl;
      }

      const smallRendering = this.svgWidth < 300 || this.svgHeight < 300;

      if (this.imageIsPng || smallRendering || !customerSpecifiedUrl) {
        return croppedAndNormalizedUrl;
      }

      return customerSpecifiedUrl;
    },
    widthInInches() {
      return this.artwork.exterior.widthInInches;
    },
    heightInInches() {
      return this.artwork.exterior.heightInInches;
    },
    centerX() {
      return this.widthInInches / 2;
    },
    centerY() {
      return this.heightInInches / 2;
    },
    imageAspectRatio() {
      if (!this.artwork.croppedAndNormalized) {
        return this.artwork.exterior.widthInInches / this.artwork.exterior.heightInInches;
      }

      return this.artwork.croppedAndNormalized.dimensionsInPixels.width / this.artwork.croppedAndNormalized.dimensionsInPixels.height;
    },
    boxAspectRatio() {
      return this.widthInInches / this.heightInInches;
    },
    innerWidth() {
      return this.widthInInches - (this.rabbetWidth * 2);
    },
    innerHeight() {
      return this.heightInInches - (this.rabbetWidth * 2);
    },
    innerAspectRatio() {
      return this.innerWidth / this.innerHeight;
    },
    rabbetWidth() {
      return this.moulding?.rabbet_width || 0;
    },
    isDigitalFrameWithoutMat() {
      return this.frameSpec.isDigital() && !this.frameSpec.hasMat() && !this.frameSpec.isFloated() && !this.frameSpec.isClearFloated();
    },
    imageWidth() {
      if (this.isDigitalFrameWithoutMat) {
        const width = this.imageAspectRatio > this.innerAspectRatio
          ? this.innerHeight * this.imageAspectRatio
          : this.innerWidth;
        return width + 0.01;
      } else {
        if (this.imageAspectRatio > this.boxAspectRatio) {
          return this.heightInInches * this.imageAspectRatio;
        } else {
          return this.widthInInches;
        }
      }
    },
    imageHeight() {
      if (this.isDigitalFrameWithoutMat) {
        const height = this.imageAspectRatio > this.innerAspectRatio
          ? this.innerHeight
          : this.innerWidth / this.imageAspectRatio;
        return height + 0.01;
      } else {
        if (this.imageAspectRatio > this.boxAspectRatio) {
          return this.heightInInches;
        } else {
          return this.widthInInches / this.imageAspectRatio;
        }
      }
    },
    imageX() {
      if (this.isDigitalFrameWithoutMat) {
        const excessWidth = this.imageWidth - this.innerWidth;
        return this.rabbetWidth - (excessWidth / 2);
      } else {
        return -((this.imageWidth - this.widthInInches) / 2);
      }
    },
    imageY() {
      if (this.isDigitalFrameWithoutMat) {
        const excessHeight = this.imageHeight - this.innerHeight;
        return this.rabbetWidth - (excessHeight / 2);
      } else {
        return -((this.imageHeight - this.heightInInches) / 2);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  
  to {
    transform: rotate(360deg);
  }
}

svg {
  align-items: center;
  display: flex;
  height: auto;
  justify-content: center;
  max-width: 100%;
  width: 100%;

  .allow-upload-icon {
    cursor: pointer;
  }

  .clickable-area {
    cursor: pointer;
    pointer-events: all;
  }

  .artwork-overlay {
    fill: #ffffff80;
  }

  .artwork-spinner {
    animation: spin 2s linear infinite;
    transform-box: fill-box;
    transform-origin: center;
  }

  .artwork-add path {
    fill: #adadad;
    transform-origin: center;
    transition: fill, transform 0.2s ease-in-out;
  }
  .artwork:hover .artwork-add path {
    fill: black;
    transform: scale(1.2);
  }
}
</style>